import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { CenteredModal } from '../../../UI/Modal/CenteredModal';
import Button from '../../../UI/V3/Button/Button';
import styles from './SecretRevealModal.module.scss';

interface SecretRevealModalProps {
    isOpen: boolean;
    /** Name of the key the credentials belong to, for context. */
    apiKeyName: string;
    clientId: string | null;
    clientSecret: string;
    /** Rotation reuses this modal; only the wording changes. */
    rotated?: boolean;
    onClose: () => void;
}

/**
 * Shows the client secret the backend returned.
 *
 * The backend stores only a digest, so this is the single moment the secret
 * exists in readable form. It is held in the caller's component state and is
 * gone once this modal closes. Closing is therefore deliberately explicit:
 * no backdrop dismissal, and the confirm button says what it costs.
 */
export const SecretRevealModal: React.FC<SecretRevealModalProps> = ({
    isOpen,
    apiKeyName,
    clientId,
    clientSecret,
    rotated = false,
    onClose
}) => {
    const [copiedField, setCopiedField] = useState<'id' | 'secret' | null>(null);

    const handleCopy = async (field: 'id' | 'secret', value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedField(field);
            window.setTimeout(() => setCopiedField(null), 2000);
        } catch (error) {
            // Clipboard can be unavailable over plain HTTP or without
            // permission. The value stays selectable on screen either way.
            setCopiedField(null);
        }
    };

    const handleClose = () => {
        setCopiedField(null);
        onClose();
    };

    const renderCredential = (label: string, value: string, field: 'id' | 'secret') => (
        <div className={styles.credential}>
            <label className={styles.credentialLabel}>{label}</label>
            <div className={styles.credentialRow}>
                <code className={styles.credentialValue}>{value}</code>
                <button
                    type="button"
                    className={styles.copyButton}
                    onClick={() => handleCopy(field, value)}
                    title={`Copy ${label.toLowerCase()}`}
                >
                    <FontAwesomeIcon icon={copiedField === field ? faCheck : faCopy} />
                </button>
            </div>
        </div>
    );

    return (
        <CenteredModal
            isOpen={isOpen}
            onClose={handleClose}
            maxWidth="medium"
            className={styles.secretRevealModal}
        >
            <CenteredModal.Header
                title={rotated ? 'New client secret' : 'API Key created'}
                showCloseButton={false}
            />

            <CenteredModal.Content className={styles.modalContent}>
                <div className={styles.warning}>
                    <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className={styles.warningIcon}
                    />
                    <div>
                        <p className={styles.warningTitle}>Copy the client secret now</p>
                        <p className={styles.warningText}>
                            This is the only time it will be shown. It is stored encrypted and cannot be retrieved again
                            — if it is lost, the only way to recover access is to rotate the key and replace the secret
                            wherever it is used.
                        </p>
                    </div>
                </div>

                <p className={styles.keyName}>{apiKeyName}</p>

                {clientId && renderCredential('Client ID', clientId, 'id')}
                {renderCredential('Client secret', clientSecret, 'secret')}

                {rotated && (
                    <p className={styles.rotatedNote}>
                        The previous credentials have been revoked and any access tokens issued from them have stopped
                        working. Rotation replaces both halves, so deploy the new client ID as well as the secret to
                        restore the integration.
                    </p>
                )}

                {/* Form Actions */}
                <div className={styles.formActions}>
                    <Button
                        label="I've copied the secret"
                        variant="black"
                        size="large"
                        onClick={handleClose}
                    />
                </div>
            </CenteredModal.Content>
        </CenteredModal>
    );
};

export default SecretRevealModal;
